import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as DrugActions from './drug.actions';
import { DrugEntity } from './drug.models';

export const DRUG_FEATURE_KEY = 'drug';

export interface State extends EntityState<DrugEntity> {
  selectedId?: string | number; // which Drug record has been selected
  loading: boolean; // Drug list is loading
  saving: boolean;
  error?: string | null; // last none error (if any)
  drugDetail: DrugEntity | null;
  drugDetailLoading: boolean;
  drugDetailError: string | null;
}

export interface DrugPartialState {
  readonly [DRUG_FEATURE_KEY]: State;
}

export function selectDrugId(drug: DrugEntity): string {
  //In this case this would be optional since primary key is id
  return drug._id;
}

export const drugAdapter: EntityAdapter<DrugEntity> = createEntityAdapter<
  DrugEntity
>({
  selectId: selectDrugId
});

export const initialState: State = drugAdapter.getInitialState({
  loading: false,
  saving: false,
  drugDetail: null,
  drugDetailLoading: false,
  drugDetailError: null
});

const drugReducer = createReducer(
  initialState,
  on(DrugActions.loadDrugs, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(DrugActions.loadDrugsSuccess, (state, { drugs }) =>
    drugAdapter.setAll(drugs, { ...state, loading: false })
  ),
  on(DrugActions.loadDrugsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(DrugActions.loadDrugByDin, state => ({
    ...state,
    drugDetailLoading: true,
    drugDetailError: null
  })),
  on(DrugActions.loadDrugByDinSuccess, (state, { drug }) => ({
    ...state,
    drugDetail: drug,
    drugDetailLoading: false,
    drugDetailError: null
  })),
  on(DrugActions.loadDrugByDinFailure, (state, { error }) => ({
    ...state,
    drugDetailLoading: false,
    drugDetailError: error
  })),
  on(DrugActions.clearDrugDetail, state => ({
    ...state,
    drugDetail: null,
    drugDetailError: null
  })),

  on(DrugActions.saveDrug, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(DrugActions.saveDrugSuccess, (state, { drug }) =>
    drugAdapter.addOne(drug, { ...state, saving: false })
  ),
  on(DrugActions.saveDrugFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  on(DrugActions.updateDrug, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(DrugActions.updateDrugSuccess, (state, { drug }) =>
    drugAdapter.updateOne(
      { id: drug._id, changes: drug },
      { ...state, saving: false }
    )
  ),
  on(DrugActions.updateDrugFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return drugReducer(state, action);
}
